import { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { grey } from "@material-ui/core/colors";
import { Button } from "components/Forms/StyledComponents";

import { EmailLogReportFields } from "@dashboard-v3/api";
import { DELIVERY_STATUS_CUTOFF } from "pages/EmailFlowLogs/types";

interface FieldsSelectProps {
  isOpen: boolean;
  dateFrom: number;
  onClose: () => void;
  onConfirm: (fields: EmailLogReportFields) => void;
}

type ReportFields = { [key in EmailLogReportFields[number]]?: boolean };

export default function ReportConfigModal(props: FieldsSelectProps) {
  const { t } = useTranslation("emailFlowLogs");
  const classes = useStyles();
  const { isOpen, dateFrom, onClose, onConfirm } = props;
  const [fields, setFields] = useState<ReportFields>(initState);
  const fieldNames = Object.keys(fields) as EmailLogReportFields;
  const allSelected = fieldNames.every(key => fields[key]);
  const selectedFields = fieldNames.filter(f => fields[f]);
  const fromDate = dayjs(dateFrom).format("YYYY-MM-DD");
  const finalStatusDisabled = fromDate < DELIVERY_STATUS_CUTOFF;

  const onChange = (field: string) => () => {
    setFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const onSelectAll = () => {
    setFields(prev => {
      const update = { ...prev };
      for (const key in prev) {
        if (key === "finalStatus" && finalStatusDisabled) continue;
        update[key] = !allSelected;
      }
      return update;
    });
  };

  const onSubmit = () => {
    const fields: EmailLogReportFields = [
      "recipient",
      "receivedAt",
      ...selectedFields,
    ];

    onConfirm(fields);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle className={classes.title} disableTypography>
        <Typography variant="h6">{t("reportsConfigModal.title")}</Typography>
        <Typography variant="caption" color="textSecondary">
          {t("reportsConfigModal.desc")}
        </Typography>
        <IconButton className={classes.closeBtn} size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className={classes.fieldsContainer}>
          <Chip
            className={classes.field}
            label={t("reportsConfigModal.fields.recipient")}
            color="primary"
            disabled
          />
          <Chip
            className={classes.field}
            label={t("reportsConfigModal.fields.receivedAt")}
            color="primary"
            disabled
          />
          {fieldNames.map(name => {
            const isDisabled = name === "finalStatus" && finalStatusDisabled;
            const label = t(`reportsConfigModal.fields.${name}`);

            if (isDisabled) {
              return (
                <Tooltip
                  key={name}
                  title={t("reportsConfigModal.finalStatusDisabled")}
                  placement="top"
                >
                  <span>
                    <Chip className={classes.field} label={label} disabled />
                  </span>
                </Tooltip>
              );
            }

            return (
              <Chip
                key={name}
                className={classes.field}
                label={label}
                color={fields[name] ? "primary" : "default"}
                onClick={onChange(name)}
                style={{
                  ...(!fields[name] && { background: "white" }),
                }}
              />
            );
          })}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          variant="text"
          wording={t(
            allSelected
              ? t("reportsConfigModal.deselect")
              : t("reportsConfigModal.selectAll")
          )}
          style={{ marginRight: "auto", marginLeft: "12px" }}
          onClick={onSelectAll}
        />
        <Button
          size="small"
          variant="text"
          wording={t("common:cancel")}
          onClick={() => onClose()}
        />
        <Button
          size="small"
          wording={t("common:create")}
          onClick={onSubmit}
          disabled={!selectedFields.length}
        />
      </DialogActions>
    </Dialog>
  );
}

function initState() {
  return {
    receivedId: false,
    receivedMsgUID: false,
    recipientMsgUID: true,
    action: true,
    actionCreatedAt: true,
    messageId: true,
    subject: true,
    sender: true,
    from: true,
    to: true,
    bytes: true,
    attachments: true,
    isTnef: true,
    finalStatus: true,
  };
}

const useStyles = makeStyles({
  title: { padding: "16px 24px 0 24px", maxWidth: "98%" },
  fieldsContainer: {
    background: grey[200],
    borderRadius: 8,
    padding: 10,
    margin: "20px 0",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "10px",
  },
  field: {
    height: 28,
    minWidth: 55,
    cursor: "pointer",
    lineHeight: "1.5",
  },
  closeBtn: { position: "absolute", top: "0.5rem", right: "0.5rem" },
});
